import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import Form from "../../components/Form";
import PropTypes from "prop-types";
import Paper from "../../components/Paper";
import { makeStyles } from "../../utils/styles";
import Typography from "../../components/Typography";
import Divider from "../../components/Divider";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FORM } from "../../utils/constans";
import Button from "../../components/Button";
import ButtonGroup from "../../components/ButtonGroup";
import Icon from "../../components/Icon";
import useTheme from "../../hooks/useTheme";
import { Col, Row } from "react-styled-flexboxgrid";
import Input from "../../components/Input";
import Select from "../../components/Select";
import InputTags from "../../components/InputTags";
import { parseUserSuppliersToOptions, preventDefault } from "../../utils/helpers";
import Place from "../../components/Place";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((css) => ({
    paper: css`
        position: relative;
        overflow: hidden;
        padding: 30px 30px 15px 30px;
        width: 940px;
    `,
    dividerHeader: css`
        margin-top: 25px;
        margin-bottom: 20px;
    `,
    dividerHeaderSecond: css`
        margin-top: 20px;
        margin-bottom: 30px;
    `,
    dividerFooter: css`
        margin-top: 30px;
        margin-bottom: 15px;
    `,
    professionsTitle: css`
        margin-bottom: 30px;
    `,
}));

const schema = yup.object().shape({
    name: yup.string().required(FORM.FIELD_IS_REQUIRED),
    manager: yup.string().required(FORM.FIELD_IS_REQUIRED),
    phone: yup.string().required(FORM.FIELD_IS_REQUIRED),
    professions_ids: yup.array().typeError(FORM.FIELD_IS_REQUIRED).required(FORM.FIELD_IS_REQUIRED),
});

const getPreloadedValues = () => ({
    professions_ids: [],
    manager: ''
});

const FormDepartment = ({ loading, onSubmit, onCancel, edit, place, availableManagers, initialValues, professionsHints }) => {
    const { t } = useTranslation()
    const styles = useStyles();
    const theme = useTheme();

    const { register, watch, handleSubmit, errors, getValues, setValue, setError, clearErrors, control } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            ...getPreloadedValues(),
            ...initialValues
        },
    });

    const watchProfessionsIds = watch("professions_ids");

    useEffect(() => {
        register({ name: "professions_ids" });
    }, [register]);

    const handleClickAddProfession = value => {
        if (value.tag) {
            const professionsIds = getValues('professions_ids');
            setValue("professions_ids", [...professionsIds, value.tag?.id]);
            clearErrors("professions_ids");
        } else if (value.error && value.message) {
            setError("professions_ids", {
                type: "manual",
                message: value.message
            });
        }
    };

    const handleClickDeleteProfession = profession => {
        const professionsIds = getValues('professions_ids');
        setValue("professions_ids", professionsIds.filter(professionID => professionID !== profession.id));
    };

    const handleClickDeleteProfessionAll = () => {
        setValue("professions_ids", []);
    };

    const professions = useMemo(() => {
        if (!!!professionsHints || watchProfessionsIds.length === 0) return [];
        return professionsHints.filter(profession => !!watchProfessionsIds.find(professionID => professionID === profession.id));
    }, [professionsHints, watchProfessionsIds]);

    const managersOptions = useMemo(() => {
        return parseUserSuppliersToOptions(availableManagers);
    }, [availableManagers]);

    return (
        <Paper
            customStyle={styles.paper}
        >
            <Form onSubmit={preventDefault}>
                <Typography as="h1">
                    {edit ? t('Edytuj dział') : t('Dodaj dział')}
                </Typography>
                <Divider customStyle={styles.dividerHeader} />
                <Place
                    name={place?.name}
                    logo={place?.logo}
                    address={place?.address}
                    typographyCmp="span"
                />
                <Divider customStyle={styles.dividerHeaderSecond} />
                <Row>
                    <Col xs={6}>
                        <Input
                            type="text"
                            ref={register}
                            name="name"
                            label={t("Nazwa działu")}
                            placeholder={t("Nazwa działu")}
                            error={!!errors.name}
                            errorText={errors.name?.message}
                        />
                        <Controller
                            control={control}
                            name="manager"
                            render={({ onChange, value }) => (
                                <Select
                                    type="text"
                                    name="manager"
                                    value={value}
                                    onChange={onChange}
                                    label={t("Manager działu")}
                                    placeholder={t("Manager działu")}
                                    options={managersOptions}
                                    error={!!errors.manager}
                                    errorText={errors.manager?.message}
                                    fullWidth
                                />
                            )}
                        />
                        <Input
                            type="number"
                            ref={register}
                            name="phone"
                            label={t("Telefon")}
                            placeholder={t("Telefon")}
                            error={!!errors.phone}
                            errorText={errors.phone?.message}
                        />
                    </Col>
                </Row>
                <Divider customStyle={styles.dividerFooter} />
                <Typography font={theme.fonts.mulishBold} as="h2" customStyle={styles.professionsTitle}>
                    {t("Zawody")}
                </Typography>
                <InputTags
                    placeholder={t("Wpisz zawód i naciśnij Enter")}
                    tags={professions}
                    tagsHints={professionsHints}
                    enableEnterKeyDown
                    enableDeleteAll={professions.length > 0}
                    useHintsToFindTag
                    onClickAdd={handleClickAddProfession}
                    onClickDelete={handleClickDeleteProfession}
                    onClickDeleteAll={handleClickDeleteProfessionAll}
                    onEnterKeyDown={handleClickAddProfession}
                    error={!!errors.professions_ids}
                    errorText={errors.professions_ids?.message}
                />
                <Divider customStyle={styles.dividerFooter} />
                <Row end="xs">
                    <Col>
                        <ButtonGroup>
                            <Button
                                variant="text"
                                onClick={onCancel}
                                startIcon={
                                    <Icon
                                        name="close"
                                        width={12}
                                        height={12}
                                        fill={theme.colors.purple}
                                    />
                                }
                            >
                                {t("Anuluj")}
                            </Button>
                            <Button
                                onClick={handleSubmit(onSubmit)}
                                startIcon={!edit ?
                                    <Icon name="add" width={12} height={12} fill={theme.colors.white} /> :
                                    <Icon name="save" width={14} height={14} fill={theme.colors.white} />
                                }
                                disabled={loading}
                            >
                                {edit ? t("Zapisz") : t("Dodaj dział")}
                            </Button>
                        </ButtonGroup>
                    </Col>
                </Row>
            </Form>
            <Loader show={loading} />
        </Paper>
    );
};

FormDepartment.propTypes = {
    loading: PropTypes.bool,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    active: PropTypes.object,
    availableManagers: PropTypes.any,
    place: PropTypes.object,
    edit: PropTypes.bool,
    professionsHints: PropTypes.array
};

export default FormDepartment;
